import { getCartSuccessLabel } from '../shared/cart-feedback-label';
import { mountQuantityStepper, type QuantityStepperInstance } from '../shared/quantity-stepper';

const ADD_TO_CART_TIMEOUT_MS = 15000;
const SUCCESS_TIMEOUT_MS = 1600;

function parseOptionUids(card: HTMLElement): string[] {
  return (card.dataset.bundleOptionUids ?? '')
    .split(',')
    .map(uid => uid.trim())
    .filter(Boolean);
}

export function getBundleQuantity(stepper: QuantityStepperInstance | null): number {
  return stepper?.getQuantity() ?? 1;
}

export default function bundlePricingOptionsBlock(root: HTMLElement): void {
  const card = root.querySelector<HTMLElement>('[data-pricing-option-card]');
  const regionSelect = root.querySelector<HTMLSelectElement>(
    'select[name="data-storage-location"]'
  );
  const addButton = root.querySelector<HTMLButtonElement>('[data-add-to-cart]');
  const buttonLabel = root.querySelector<HTMLElement>('[data-button-label]');
  const feedback = root.querySelector<HTMLElement>('[data-pricing-option-feedback]');
  const status = root.querySelector<HTMLElement>('[data-pricing-option-status]');

  if (!card || !regionSelect || !addButton || !buttonLabel) return;

  const stepper = mountQuantityStepper(card, { clearIcon: true });

  const sku = card.dataset.sku?.trim();
  const planName = card.dataset.planName?.trim() || 'bundle';
  const optionUids = parseOptionUids(card);
  const unavailableMessage = card.dataset.unavailableMessage?.trim();
  const defaultLabel = buttonLabel.textContent?.trim() || '';

  const clearFeedback = (): void => {
    if (!feedback) return;
    feedback.hidden = true;
    feedback.textContent = '';
    feedback.removeAttribute('data-feedback-state');
  };

  const selectedRegionUid = (): string | undefined =>
    regionSelect.selectedOptions[0]?.dataset.optionUid?.trim() || undefined;

  const syncAvailability = (): void => {
    const isUnavailable = Boolean(unavailableMessage);
    addButton.disabled = isUnavailable || !sku || optionUids.length === 0 || !selectedRegionUid();
    regionSelect.setAttribute('aria-invalid', selectedRegionUid() ? 'false' : 'true');

    if (isUnavailable && feedback) {
      feedback.hidden = false;
      feedback.dataset.feedbackState = 'error';
      feedback.textContent = unavailableMessage ?? '';
    }
  };

  regionSelect.addEventListener('change', () => {
    clearFeedback();
    syncAvailability();
  });

  addButton.addEventListener('click', () => {
    const regionUid = selectedRegionUid();
    if (!sku || !regionUid || optionUids.length === 0 || unavailableMessage) {
      syncAvailability();
      return;
    }

    clearFeedback();
    addButton.disabled = true;
    addButton.dataset.feedbackState = 'loading';
    const requestId = `${sku}-${Date.now()}`;

    const onResult = (event: Event): void => {
      const result = event as CustomEvent<{ requestId: string; success: boolean; error?: string }>;
      if (result.detail.requestId !== requestId) return;

      window.clearTimeout(timeout);
      document.removeEventListener('bentley:add-to-cart-result', onResult);

      if (result.detail.success) {
        addButton.dataset.feedbackState = 'success';
        const successLabel = getCartSuccessLabel(card, defaultLabel);
        buttonLabel.textContent = successLabel;
        if (status) status.textContent = successLabel;
        window.setTimeout(() => {
          addButton.removeAttribute('data-feedback-state');
          buttonLabel.textContent = defaultLabel;
          syncAvailability();
        }, SUCCESS_TIMEOUT_MS);
        return;
      }

      addButton.removeAttribute('data-feedback-state');
      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent = result.detail.error || 'Unable to add this bundle right now.';
      }
      if (status) status.textContent = `Unable to add ${planName} to cart.`;
      syncAvailability();
    };

    document.addEventListener('bentley:add-to-cart-result', onResult);
    const timeout = window.setTimeout(() => {
      document.removeEventListener('bentley:add-to-cart-result', onResult);
      addButton.removeAttribute('data-feedback-state');
      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent = 'Unable to add this bundle right now.';
      }
      syncAvailability();
    }, ADD_TO_CART_TIMEOUT_MS);

    document.dispatchEvent(
      new CustomEvent('bentley:add-to-cart-request', {
        detail: {
          requestId,
          planName,
          items: [
            {
              sku,
              quantity: getBundleQuantity(stepper),
              selected_options: [...optionUids, regionUid]
            }
          ]
        }
      })
    );
  });

  syncAvailability();
}
